import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  type QualificationAnswerPayload,
  type QualificationOptionsPayload,
} from '../api';
import { QualificationOptions } from './QualificationOptions';

/**
 * "Your turn": a lead question offered as the last suggestion chip.
 *
 * Arrives on `your_turn` after the suggestions, on a reply that did not ask
 * anything itself — the backend offers it when the visitor's message was about
 * this question's subject. It is optional in every way: tapping the row opens
 * the same answer buttons a question in the reply gets, tapping it again folds
 * them away, and ignoring it is simply asking something else. Nothing is sent
 * until an option is picked.
 *
 * Styled like the plain suggestion rows above it (same text style, same
 * dashed underline, no "Your turn" badge) but with two tells that it is a
 * control rather than just another thing to ask: the question in the brand
 * colour with a chevron at the end.
 */
export function YourTurnChip({
  data,
  theme,
  primaryColor,
  disabled,
  expanded,
  textSize,
  onAnswer,
  onChooseCustom,
}: {
  data: QualificationOptionsPayload;
  theme: 'light' | 'dark';
  primaryColor: string;
  disabled: boolean;
  expanded?: boolean;
  /**
   * Question font size. Defaults to the chat widget's suggestion size
   * (16px expanded / 13px collapsed); the full-screen view passes its own,
   * since its suggestion rows use a different scale.
   */
  textSize?: string;
  onAnswer: (text: string, payload: QualificationAnswerPayload) => void;
  /** Same contract as QualificationOptions: point the composer at this question, or release it. */
  onChooseCustom: (data: QualificationOptionsPayload | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const dark = theme === 'dark';
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The chip sits at the bottom of the conversation, so its options open below
  // the fold. Bring them into view once, when opened — a tap that seems to do
  // nothing is read as broken.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(
      () => wrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
      60
    );
    return () => clearTimeout(timer);
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    // Folding the options away also releases a pending "Something else", so a
    // closed chip never leaves the composer answering a question out of sight.
    if (open) onChooseCustom(null);
    setOpen(!open);
  };

  return (
    <div ref={wrapperRef} className="vw-w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={toggle}
        aria-expanded={open}
        title="A quick question for you — tap to answer, or just ignore it"
        className="vw-animate-in vw-text-left hover:vw-opacity-70 vw-transition-opacity vw-duration-200 vw-group vw-w-fit vw-max-w-full vw-bg-transparent vw-p-0 disabled:vw-opacity-50"
        style={{ animationDelay: '140ms', animationFillMode: 'backwards' }}
      >
        <span className="vw-flex vw-items-start vw-gap-2">
          <span
            className="vw-min-w-0 vw-mb-1 vw-leading-tight vw-break-words"
            style={{
              color: primaryColor,
              fontSize: textSize ?? (expanded ? '16px' : '13px'),
              fontWeight: 500,
            }}
          >
            {data.question}
          </span>
          <ChevronDown
            className={`vw-h-4 vw-w-4 vw-shrink-0 vw-mt-0.5 vw-transition-transform ${open ? 'vw-rotate-180' : ''}`}
            style={{ color: primaryColor }}
          />
        </span>
        <div className={`vw-border-b vw-border-dashed ${dark ? 'vw-border-gray-600' : 'vw-border-gray-300'} vw-w-full`} />
      </button>

      {open && (
        <QualificationOptions
          data={data}
          theme={theme}
          primaryColor={primaryColor}
          disabled={disabled}
          expanded={expanded}
          onAnswer={onAnswer}
          onChooseCustom={onChooseCustom}
          onSkip={toggle}
          hideQuestionHeader={true}
        />
      )}
    </div>
  );
}
